import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PLACEHOLDERS } from '@/lib/placeholders';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const ICON_URL = PLACEHOLDERS.appIcon;

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [showAndroidPrompt, setShowAndroidPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const { isDark } = useTheme();
  const location = useLocation();
  const hideOnStandalone =
    location.pathname.startsWith('/vendor') ||
    location.pathname.startsWith('/story-studio') ||
    location.pathname.startsWith('/admin');

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if user has already dismissed
    const hasDismissed = localStorage.getItem('pwa-install-dismissed');
    if (hasDismissed) return;

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowAndroidPrompt(true);
    };

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isIOS && isSafari && !window.matchMedia('(display-mode: standalone)').matches) {
      setTimeout(() => setShowIOSPrompt(true), 2500);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowAndroidPrompt(false);
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShowAndroidPrompt(false);
    setShowIOSPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  }, []);

  if (isInstalled || hideOnStandalone) return null;

  const showBanner = showAndroidPrompt || showIOSPrompt;

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className={`fixed bottom-4 left-4 right-4 z-[9999] rounded-2xl p-4 shadow-lg backdrop-blur-md ${
            isDark
              ? 'bg-dark-100/95 text-white border border-dark-200/50'
              : 'bg-white/95 text-gray-900 border border-gray-200/50'
          }`}
          style={{ direction: 'rtl' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-dark-200">
              <img
                src={ICON_URL}
                alt="BAZARAK"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-sm leading-tight">
                نصب برنامه BAZARAK
              </h3>
              <p className="text-xs opacity-70 mt-1 leading-relaxed">
                {showIOSPrompt
                  ? 'برای نصب، دکمه Share را بزنید و گزینه Add to Home Screen را انتخاب کنید'
                  : 'این برنامه را به صفحه اصلی گوشی خود اضافه کنید برای تجربه سریع‌تر'}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {showAndroidPrompt && (
                <button
                  onClick={handleInstallClick}
                  className="px-4 py-2 rounded-full bg-gold text-white text-xs font-bold whitespace-nowrap active:scale-95 transition-transform"
                >
                  نصب
                </button>
              )}
              <button
                onClick={handleDismiss}
                className={`w-8 h-8 flex items-center justify-center rounded-full ${
                  isDark ? 'bg-dark-200 text-gray-300' : 'bg-gray-100 text-gray-500'
                } active:scale-90 transition-transform`}
                aria-label="بستن"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-close-line text-base" />
                </div>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}